import { z } from "zod";

// Research tools: find a scraper on the store, read its inputs, run it under a cost cap, page the records.

export const researchJobStatusSchema = z
  .enum(["queued", "running", "ready", "failed"])
  .describe("ResearchJobStatus");

export type ResearchJobStatus = z.infer<typeof researchJobStatusSchema>;

/** `owner/name` on the Apify store; the only source id the agent handles. */
export const researchSourceIdSchema = z
  .string()
  .regex(
    /^[\w-]+(?:\.[\w-]+)*\/[\w-]+(?:\.[\w-]+)*$/,
    "Expected a source id shaped owner/name",
  );

export const RESEARCH_MAX_CREDITS_PER_RUN = 600;

/** One scraped record, as the scraper produced it. */
export const researchRecordSchema = z.record(z.string(), z.unknown());

export type ResearchRecord = z.infer<typeof researchRecordSchema>;

/** One R2 document per run holds every record. */
export const researchJobDocumentSchema = z.object({
  jobId: z.uuid(),
  runId: z.string(),
  source: z.string(),
  items: z.array(researchRecordSchema),
});

export type ResearchJobDocument = z.infer<typeof researchJobDocumentSchema>;

export const researchJobEnvelopeSchema = z
  .object({
    jobId: z.uuid(),
    status: researchJobStatusSchema,
    source: z.string(),
    itemCount: z.number().int(),
    cached: z
      .boolean()
      .describe("True when served from a job under 14 days old."),
    creditsCharged: z
      .number()
      .int()
      .nullable()
      .describe("Credits charged to this workspace for this call, once known."),
    items: z.array(researchRecordSchema),
    nextAction: z.string().nullable(),
  })
  .describe("ResearchJobEnvelope");

export type ResearchJobEnvelope = z.infer<typeof researchJobEnvelopeSchema>;

export const researchFindSourceInputSchema = z.object({
  question: z
    .string()
    .min(3)
    .max(200)
    .describe(
      "What evidence you need, e.g. 'amazon reviews for a product' or 'trustpilot reviews for a brand'.",
    ),
});

export const researchSourceSummarySchema = z
  .object({
    sourceId: researchSourceIdSchema,
    pinned: z
      .boolean()
      .describe("A source Replo chose for this kind of question. Prefer it."),
    title: z.string(),
    summary: z.string(),
    typicalCostCredits: z
      .number()
      .int()
      .describe("Start fee plus 100 items at this source's per-item price."),
    minCostCredits: z
      .number()
      .int()
      .describe("The source refuses runs with a smaller budget than this."),
    users: z
      .number()
      .int()
      .nullable()
      .describe("Accounts that have run this source; a maintenance signal."),
    successRate30d: z.number().nullable(),
  })
  .describe("ResearchSourceSummary");

export type ResearchSourceSummary = z.infer<typeof researchSourceSummarySchema>;

export const researchFindSourceResultSchema = z
  .object({
    sources: z.array(researchSourceSummarySchema),
    nextAction: z.string(),
  })
  .describe("ResearchFindSourceResult");

export type ResearchFindSourceResult = z.infer<
  typeof researchFindSourceResultSchema
>;

export const researchDescribeSourceInputSchema = z.object({
  sourceId: researchSourceIdSchema,
});

export const researchSourceFieldSchema = z
  .object({
    key: z.string(),
    type: z.string(),
    required: z.boolean(),
    description: z.string().nullable(),
    default: z.unknown(),
    enum: z.array(z.string()).nullable(),
    example: z.unknown(),
  })
  .describe("ResearchSourceField");

export type ResearchSourceField = z.infer<typeof researchSourceFieldSchema>;

export const researchSourceDescriptionSchema = z
  .object({
    sourceId: researchSourceIdSchema,
    title: z.string(),
    fields: z.array(researchSourceFieldSchema),
    startCredits: z.number().int(),
    perItemCredits: z.number(),
    minCostCredits: z.number().int(),
    exampleInput: researchRecordSchema,
    nextAction: z.string(),
  })
  .describe("ResearchSourceDescription");

export type ResearchSourceDescription = z.infer<
  typeof researchSourceDescriptionSchema
>;

export const researchRunSourceInputSchema = z.object({
  sourceId: researchSourceIdSchema,
  input: researchRecordSchema.describe(
    "Fields from research_describe_source for this source.",
  ),
  maxCredits: z
    .number()
    .int()
    .min(1)
    .max(RESEARCH_MAX_CREDITS_PER_RUN)
    .optional()
    .describe(
      "Budget for this run in credits; defaults to the workspace's headroom, up to 600.",
    ),
});

export const researchGetJobInputSchema = z.object({
  jobId: z.uuid(),
  offset: z.number().int().min(0).default(0),
  limit: z.number().int().min(1).max(100).default(50),
});
